/**
 * FAQ Chat Service
 *
 * Answers product questions from a curated, client-side repository
 * (FAQ_REPOSITORY + findFAQAnswer). No external LLM is called: the widget
 * matches the user's question against the repo and falls back to an "email us"
 * reply.
 *
 * Note: an earlier Gemini live-API path (sendFAQMessage, the client-side
 * REACT_APP_GEMINI_API_KEY read, and an embedded product-context prompt) was
 * removed. It was unused by the app and would have exposed an API key in the
 * browser bundle. Keep this file key-free; route any real LLM call through the
 * backend instead.
 */

/**
 * Curated FAQ repository used by the support chat widget.
 *
 * The chat widget answers from this list without hitting an API. If a user's
 * free-text question matches any entry (loose substring match), we surface that
 * answer; otherwise we fall back to a friendly "email us" reply.
 */
export const FAQ_REPOSITORY = [
  {
    id: 'what-is-restack',
    question: 'What is Restack?',
    answer:
      "Restack is AI for Private Equity teams. It builds your actual deliverable, the IC deck, the model, the board memo, to the bar a committee accepts. Open it from the ribbon inside your live Office files (PowerPoint, Excel, Word), chat in plain English, and it researches, cites, and builds in your file, then carries the work between apps when you ask. Where a generic AI hands you a rough draft, Restack renders the chart grammar PE decks run on (waterfalls, Mekkos, bridges), holds your firm's template, and checks its own work before you see it.",
  },
  {
    id: 'how-works',
    question: 'How does it work?',
    answer:
      "You describe what you need (e.g. \"Build a TAM/SAM/SOM slide for Nordic fintech\"). Restack scans up to 100 sources, cross-validates the data, and builds inside your Office file: the deck in PowerPoint, the model in Excel, or the document in Word. From there you edit normally, and can ask it to carry a table or summary into another app.",
  },
  {
    id: 'different-from-chatgpt',
    question: 'How is this different from ChatGPT?',
    answer:
      "ChatGPT gives you text or rough drafts that you copy into Office. Restack runs inside your live PowerPoint, Excel, and Word files, writing into your .pptx, .xlsx, or .docx with data, charts, citations, and formatting. Every fact includes a source you can verify.",
  },
  {
    id: 'research-quality',
    question: 'How good is the research?',
    answer:
      "Every slide scans up to 100 sources (market reports, filings, APIs, news), cross-validates metrics, and reports a confidence score. Each data point keeps its citation - dozens of cited sources per deliverable is normal.",
  },
  {
    id: 'frameworks',
    question: 'What frameworks does it support?',
    answer:
      "Restack knows the strategy canon - TAM/SAM/SOM, Porter's Five Forces, SWOT, BCG Matrix, McKinsey 7S, Value Chain, PESTEL, Ansoff, due-diligence structures - and lays each one out correctly on your template, cited. If your firm runs its own frameworks, point it at a reference slide and it follows your format.",
  },
  {
    id: 'pricing',
    question: 'How does pricing work?',
    answer:
      "One plan: Pro at $800 per seat per month, billed monthly. 800 credits per seat, pooled across your team and shared across all three apps. 1 to 5 seats self-serve; 6+ or firm-wide is Practice. Email hi@re-stack.com.",
  },
  {
    id: 'security',
    question: 'Is my data secure?',
    answer:
      "Restack encrypts stored data with Google-managed keys and uses TLS 1.2+ in transit. Your inputs are not used for model training. Live Office files remain in Microsoft 365, while chat and session records plus uploaded source files use encrypted Restack storage. SOC 2 Type II and ISO 27001 are in progress. Practice security and deployment requirements are reviewed during contracting.",
  },
  {
    id: 'getting-started',
    question: 'How do I get started?',
    answer:
      "Tap \"See it on your own deck\" or \"Live on Microsoft AppSource\" at the top of this page. Once you install the add-in, open PowerPoint, Excel, or Word, click the Restack tab in the ribbon, and sign in with Microsoft or your email. You'll be building in a few minutes.",
  },
  {
    id: 'powerpoint-versions',
    question: 'Which apps and versions work?',
    answer:
      "PowerPoint, Excel, and Word for Microsoft 365 (Windows, Mac, web) and Office 2021+. PowerPoint is the most mature surface; Excel and Word run on the same engine and are newer. You need a Microsoft account that can install Office add-ins, which any standard work account handles.",
  },
  {
    id: 'collaboration',
    question: 'Can my team collaborate?',
    answer:
      "Yes. The Pro plan is built for deal teams: pick 1 to 5 seats with credits pooled across everyone and all three apps, so the whole engagement draws from one balance, plus project codes on receipts for clean billing. Need 6 or more seats or a sales-assisted rollout? The Practice plan adds a contracted seat count, invoicing and annual agreements, and custom DPA and contract-reviewed compliance terms.",
  },
  {
    id: 'refund',
    question: 'What if it is not the right fit?',
    answer:
      "We offer a 30-day money-back guarantee. If Restack doesn't earn its place in your workflow within the first month, email hi@re-stack.com and we refund the subscription, no questions asked.",
  },
  {
    id: 'contact',
    question: 'How do I contact the team?',
    answer:
      "Email hi@re-stack.com for anything: pricing, enterprise, bug reports, or roadmap input. We reply within 24 hours, and you'll be talking to the founder directly.",
  },
];

/**
 * Find the best matching repository entry for a free-text question.
 * Uses a lightweight lowercased substring match on question text + a few
 * keyword aliases so casual phrasing ("pricing?", "secure data") still lands.
 */
export function findFAQAnswer(text) {
  if (!text) return null;
  const needle = text.toLowerCase().trim();

  // Exact question match first
  const exact = FAQ_REPOSITORY.find(
    (item) => item.question.toLowerCase() === needle
  );
  if (exact) return exact;

  // Keyword aliases → id
  const keywordMap = [
    { keywords: ['price', 'pricing', 'cost', 'how much', 'plan'], id: 'pricing' },
    { keywords: ['secure', 'security', 'privacy', 'data', 'gdpr', 'soc'], id: 'security' },
    { keywords: ['start', 'begin', 'install', 'try', 'demo', 'signup', 'sign up'], id: 'getting-started' },
    { keywords: ['what', 'tell me', 'about restack'], id: 'what-is-restack' },
    { keywords: ['research', 'sources', 'citation', 'accura'], id: 'research-quality' },
    { keywords: ['framework', 'tam', 'porter', 'swot', 'bcg', 'mckinsey'], id: 'frameworks' },
    { keywords: ['chatgpt', 'openai', 'compare', 'different'], id: 'different-from-chatgpt' },
    { keywords: ['team', 'collab', 'seat', 'share'], id: 'collaboration' },
    { keywords: ['version', 'mac', 'windows', 'office', '365', 'web', 'excel', 'word', 'apps'], id: 'powerpoint-versions' },
    { keywords: ['refund', 'cancel', 'money back', 'guarantee'], id: 'refund' },
    { keywords: ['contact', 'email', 'support', 'reach', 'help'], id: 'contact' },
    { keywords: ['how does it work', 'how it works', 'workflow', 'how'], id: 'how-works' },
  ];

  for (const { keywords, id } of keywordMap) {
    if (keywords.some((kw) => needle.includes(kw))) {
      return FAQ_REPOSITORY.find((item) => item.id === id) || null;
    }
  }

  return null;
}

export const FAQ_FALLBACK_ANSWER =
  "Great question. For anything I haven't covered, email hi@re-stack.com and the team will get back to you within 24 hours. You can also tap any of the questions above for a quick answer.";

/**
 * Back-compat: older code expected a flat list of suggestions.
 * Return the first few questions from the repository.
 */
export const FAQ_SUGGESTIONS = FAQ_REPOSITORY.slice(0, 4).map((q) => q.question);

export default {
  FAQ_REPOSITORY,
  FAQ_SUGGESTIONS,
  FAQ_FALLBACK_ANSWER,
  findFAQAnswer,
};
